import * as React from "react";
import {
  Stack,
  SearchBox,
  PrimaryButton,
  Fabric,
  DetailsList,
  SelectionMode,
  DetailsListLayoutMode,
  IColumn,
  TooltipHost,
  IDetailsListProps,
  IDetailsRowStyles,
  DetailsRow,
  Dropdown,
  DatePicker,
  DayOfWeek,
  DefaultButton,
} from "office-ui-fabric-react";
import { getAuditLogs, exportAuditLogsExcel } from "../../services/audit/AuditLogService";
import { IAuditLog, IAuditLogFilters } from "../../vm";
import { Loading } from "../../services/Loading";
import Icon from "@mdi/react";
import { mdiFilterOutline, mdiDownloadOutline } from "@mdi/js";
import { THEME } from "../../utils/Styles";
import { childrenGap, AUDIT_ACTION_OPTIONS, AUDIT_ACTION_COLOR, AUDIT_ACTION_OPTIONS_DICT, AUDIT_RESOURCE_OPTIONS, AUDIT_RESOURCE_DICT } from "../common/Constants";
import AuditLogDiffPanel from "./AuditLogDiffPanel";
import CustomPagination from "../common/CustomPagination";
import NoDataTile from "../common/NoDataTile";
import moment from "moment";
import { ToastContext } from "../common/ToastProvider";

const AuditLogTab: React.FC = () => {
  // state
  const [state, setState] = React.useState({
    logs: [],
    total: 0,
    isLoading: false,
    showFilters: false,
    expandedLog: undefined,
  } as {
    logs: IAuditLog[];
    total: number;
    isLoading: boolean;
    showFilters: boolean;
    expandedLog: IAuditLog | undefined;
  });

  const [filters, setFilters] = React.useState<IAuditLogFilters>({
    offset: 0,
    limit: 20,
  });

  // custom hooks
  const { showToast } = React.useContext(ToastContext);

  React.useEffect(() => {
    loadLogs(filters, true);
  }, []);

  const loadLogs = async (currentFilters: IAuditLogFilters, showLoading = false) => {
    if (showLoading) setState(prev => ({ ...prev, isLoading: true }));
    const result = await getAuditLogs(currentFilters);
    if (result?.success) {
      setState(prev => ({
        ...prev,
        logs: result.data.data,
        total: result.data.total,
        isLoading: false,
      }));
      setFilters(currentFilters);
    } else {
      showToast(result.message || "Error while getting audit logs", "error");
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleFilterChange = (partial: Partial<IAuditLogFilters>) => {
    const updated = { ...filters, ...partial, offset: 0 };
    loadLogs(updated);
  };

  const handleClearFilters = () => {
    const defaultFilters: IAuditLogFilters = { offset: 0, limit: 20 };
    setFilters(defaultFilters);
    loadLogs(defaultFilters, true);
  };

  const onExport = async () => {
    await exportAuditLogsExcel(filters);
  };

  const _onRenderRow: IDetailsListProps["onRenderRow"] = (props) => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (props) {
      if (props.itemIndex % 2 !== 0) {
        customStyles.root = { backgroundColor: "#F3F3F3" };
      }
      return <DetailsRow {...props} styles={customStyles} />;
    }
    return null;
  };

  const _renderItemColumn = (item: IAuditLog, index: number, column: IColumn) => {
    switch (column.key) {
      case "createdAt":
        return (
          <span style={{ fontWeight: 600 }}>
            {moment(item.createdAt).format("DD/MM/YYYY hh:mm:ss A")}
          </span>
        );
      case "user":
        return (
          <Stack tokens={{ childrenGap: 4 }}>
            <span>{item.userName || "—"}</span>
            <span style={{ fontSize: 12, color: "#605e5c" }}>{item.userEmail || "—"}</span>
          </Stack>
        );
      case "action":
        return (
          <span style={{ color: AUDIT_ACTION_COLOR[item.action] || "#323130" }}>
            {AUDIT_ACTION_OPTIONS_DICT[item.action] || item.action}
          </span>
        );
      case "resource":
        return (
          <span>
            {AUDIT_RESOURCE_DICT[item.resource] || item.resource}
          </span>
        );
      case "status":
        return (
          <span style={{ color: item.statusCode && item.statusCode < 400 ? "#107c10" : "#c50f1f", fontWeight: 600 }}>
            {item.statusCode || "—"}
          </span>
        );
      case "datacenter":
        return <span>{item.datacenterName || "—"}</span>;
      case "customer":
        return <span>{item.customerName || "—"}</span>;
      case "diff":
        const hasDiff = (item.previousState && Object.keys(item.previousState).length > 0) ||
          (item.newState && Object.keys(item.newState).length > 0);
        return hasDiff ? (
          <DefaultButton
            text="View"
            styles={{ root: { height: 24, minWidth: 50, padding: "0 8px", fontSize: 12 } }}
            onClick={() => setState(prev => ({ ...prev, expandedLog: item }))}
          />
        ) : null;
      default:
        return (
          <span>
            {column.fieldName ? (item as any)[column.fieldName] || "—" : "—"}
          </span>
        );
    }
  };

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      <Stack tokens={childrenGap} style={{ marginTop: 16 }}>
        <Stack.Item>
          <Stack horizontal wrap horizontalAlign="space-between" verticalAlign="center" tokens={childrenGap}>
            <Stack.Item>
              <Stack horizontal wrap verticalAlign="center" tokens={childrenGap}>
                <Stack.Item>
                  <SearchBox
                    placeholder="Search by email, datacenter & customer..."
                    styles={{ root: { width: 300 } }}
                    onChange={(_, val) => handleFilterChange({ search: val })}
                  />
                </Stack.Item>
                <Stack.Item className="filter-btn">
                  <TooltipHost content="Audit Filters">
                    <span onClick={() => setState(prev => ({ ...prev, showFilters: !prev.showFilters }))}>
                      <Icon
                        path={mdiFilterOutline}
                        size={1.0}
                        className="pointer"
                        color={THEME.palette.neutralLighterAlt}
                      />
                    </span>
                  </TooltipHost>
                </Stack.Item>
                {state.showFilters && (
                  <>
                    <Stack.Item>
                      <DatePicker
                        placeholder="Start Date"
                        firstDayOfWeek={DayOfWeek.Sunday}
                        showMonthPickerAsOverlay={true}
                        showGoToToday={true}
                        value={filters.startDate ? new Date(filters.startDate) : undefined}
                        onSelectDate={date => handleFilterChange({ startDate: date ? moment(date).format("YYYY-MM-DD") : undefined })}
                        styles={{ root: { width: 160 } }}
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <DatePicker
                        placeholder="End Date"
                        firstDayOfWeek={DayOfWeek.Sunday}
                        showMonthPickerAsOverlay={true}
                        showGoToToday={true}
                        value={filters.endDate ? new Date(filters.endDate) : undefined}
                        onSelectDate={date => handleFilterChange({ endDate: date ? moment(date).format("YYYY-MM-DD") : undefined })}
                        styles={{ root: { width: 160 } }}
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <Dropdown
                        placeholder="Action"
                        options={AUDIT_ACTION_OPTIONS}
                        styles={{ root: { width: 120 } }}
                        selectedKey={filters.action || ""}
                        onChange={(_, opt) => handleFilterChange({ action: opt?.key as string || undefined })}
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <Dropdown
                        placeholder="Resource"
                        options={AUDIT_RESOURCE_OPTIONS}
                        styles={{ root: { width: 180 } }}
                        selectedKey={filters.resource || ""}
                        onChange={(_, opt) => handleFilterChange({ resource: opt?.key as string || undefined })}
                      />
                    </Stack.Item>
                  </>
                )}
                <Stack.Item>
                  <DefaultButton
                    text="Clear"
                    styles={{ root: { height: 32 } }}
                    onClick={handleClearFilters}
                  />
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <PrimaryButton
                text="Export Excel"
                iconProps={{ iconName: "Download" }}
                onClick={onExport}
                onRenderIcon={() => <Icon path={mdiDownloadOutline} size={0.7} color="white" style={{ marginRight: 8 }} />}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Fabric>
            {state.logs && state.logs.length > 0 ? (
              <DetailsList
                items={state.logs}
                columns={[
                  { key: "createdAt", name: "Date / Time", fieldName: "createdAt", minWidth: 200, maxWidth: 250, isResizable: true },
                  { key: "user", name: "User", fieldName: "user", minWidth: 160, maxWidth: 200, isResizable: true },
                  { key: "action", name: "Action", fieldName: "action", minWidth: 80, maxWidth: 100, isResizable: true },
                  { key: "resource", name: "Resource", fieldName: "resource", minWidth: 140, maxWidth: 180, isResizable: true },
                  { key: "status", name: "Status", fieldName: "status", minWidth: 60, maxWidth: 80, isResizable: true },
                  { key: "datacenter", name: "Datacenter", fieldName: "datacenter", minWidth: 120, maxWidth: 160, isResizable: true },
                  { key: "customer", name: "Customer", fieldName: "customer", minWidth: 160, maxWidth: 220, isResizable: true },
                  { key: "diff", name: "Changes", fieldName: "diff", minWidth: 70, maxWidth: 80, isResizable: true },
                ]}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.justified}
                onRenderItemColumn={_renderItemColumn as any}
                onRenderRow={_onRenderRow}
                compact={true}
              />
            ) : !state.isLoading && (
              <NoDataTile title="No Audit Logs Found" />
            )}
          </Fabric>
        </Stack.Item>
        {state.total > 0 && (
          <Stack.Item>
            <CustomPagination
              totalCount={state.total}
              search={{ limit: filters.limit, offset: filters.offset || 0 }}
              name="logs"
              getFrom={(offset: number) => loadLogs({ ...filters, offset })}
            />
          </Stack.Item>
        )}
      </Stack>
      {state.expandedLog && (
        <AuditLogDiffPanel
          log={state.expandedLog}
          onDismiss={() => setState(prev => ({ ...prev, expandedLog: undefined }))}
        />
      )}
    </React.Fragment>
  );
};

export default AuditLogTab;
